import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import type { IGetUsersServiceParams } from '@/api/user';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import type { ReactSelectOption } from '@/types/Constants';
import { emailRegEx } from '@/utils/RegularExpressions';

const activeFilterSelectOptions = [
  {
    label: 'True',
    value: 'true',
  },
  {
    label: 'False',
    value: 'false',
  },
];

const validationSchema = Yup.object({
  name: Yup.string().max(60, 'This must be less than 60 characters'),
  email: Yup.string().max(60, 'This must be less than 60 characters').matches(emailRegEx, 'Invalid Email'),
  title_id: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).nullable(),
  active: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).nullable(),
});

interface IUserFilterFormData {
  name: string;
  email: string;
  title_id: ReactSelectOption;
  active: ReactSelectOption;
}

interface IUserFilterFormProps {
  handleModal: () => void;
  applyFilters: (filterData: Partial<IGetUsersServiceParams>) => void;
  defaultValues: any;
}

export default function UserFilterForm({ handleModal, applyFilters, defaultValues }: IUserFilterFormProps) {
  const getAccountTitles = useOrgTitles({});
  const accountTitles = getAccountTitles?.data?.data;
  const accountTitlesFilterSelectOptions = accountTitles?.map((title) => ({
    label: title.name,
    value: title.id,
  }));
  const onSubmit = (formData: IUserFilterFormData) => {
    const filterPayload = {
      ...(formData?.name && { name: formData?.name }),
      ...(formData?.email && { email: formData?.email }),
      ...(formData?.title_id?.value && {
        title_id: formData?.title_id?.value,
      }),
      ...(formData?.active?.value && { active: formData?.active?.value }),
    };
    applyFilters(filterPayload);
  };

  const transformedDefaultValues = {
    ...defaultValues,
    ...(defaultValues?.title_id && {
      title_id: accountTitlesFilterSelectOptions?.find((o) => o.value.toString() === defaultValues?.title_id),
    }),
    ...(defaultValues?.active && {
      active: activeFilterSelectOptions.find((o: ReactSelectOption) => o.value === defaultValues?.active),
    }),
  };
  return (
    <Modal>
      <HookFormProvider resolver={yupResolver(validationSchema)} mode='all' defaultValues={transformedDefaultValues}>
        <HookForm onSubmit={onSubmit}>
          <h2 className='mt-0 text-primaryColor-normal'>User Filter</h2>
          <section className='space-y-4'>
            <HookInput label='Full Name' name='name' type='text' />
            <HookInput label='Email' name='email' type='email' />
            <HookReactSingleSelect id='title' name='title_id' label='Title' options={accountTitlesFilterSelectOptions} isClearable={true} />
            <HookReactSingleSelect id='active' name='active' label='Active' options={activeFilterSelectOptions} isClearable={true} />
          </section>
          <div className='flex justify-end gap-4 mt-4'>
            <Button label='Cancel' btnStyle='btn-neutral' btnType='button' onClick={handleModal} />
            <Button label='Apply' btnStyle='btn-primary' btnType='submit' />
          </div>
        </HookForm>
      </HookFormProvider>
    </Modal>
  );
}
